/**
 * ./exercise2/Exercise2Workout.js
 * 
 * This module manages the workout phase in Exercise 2. 
 * It integrates **breathing patterns**, **word flash exercises**, and **animated visual guides**.
 * 
 * Similar with Exercise 1 but contain: 
 * - **Word Flash Association:**
 *   - Displays **single words** at regular intervals.
 *   - Words change **in sync with breathing cycles**.
 *   - Uses **FALLBACK_WORDS** as default word data.
 *   - Ensures **shuffled random words** on every session.
 * 
 */


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Exercise2Workout.css'; // Importing the CSS file for styling
import Circle1 from '../components/icons/circlein1.svg';
import Circle2 from '../components/icons/circlein2.svg';
import Circle3 from '../components/icons/circlein3.svg';
import Circle4 from '../components/icons/circlein4.svg';
import Circle5 from '../components/icons/circlein5.svg';
import Circle6 from '../components/icons/circlein6.svg';
import Circle7 from '../components/icons/circlein7.svg';

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";

import person1 from '../components/icons/person1.svg';
import person2 from '../components/icons/person2.svg';
import person3 from '../components/icons/person3.svg';
import person4 from '../components/icons/person4.svg';
import person5 from '../components/icons/person5.svg';
import person6 from '../components/icons/person6.svg';
import person7 from '../components/icons/person7.svg';

import CloseIcon from '../components/icons/close.svg';
import InfoIcon from '../components/icons/InfoIcon.svg'; 
import PlayIcon from '../components/icons/play.svg';
import StopIcon from '../components/icons/stop.svg';
import Curtain from '../components/Curtain'; // Import the Curtain 7
import { FALLBACK_WORDS } from '../components/fallbackWords';
import { updateUserProgress } from '../utility';
import { DEBUG_TIME } from "../config";

function Exercise2Workout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [long, setLong] = useState(3.0);
  const [short, setShort] = useState(2.0);
  const [hold, setHold] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timeRef = useRef(0);
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(true);
  const [showStopIcon, setShowStopIcon] = useState(false);
  const [instructionText, setInstructionText] = useState('Play');

  // Keep these states for word functionality
  const [words, setWords] = useState([]);
  const [currentWord, setCurrentWord] = useState('Play');
  const [wordIndex, setWordIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [wordChangeInterval, setWordChangeInterval] = useState(1000);
  const [isLoadingMoreWords, setIsLoadingMoreWords] = useState(false);
  const [debugWords, setDebugWords] = useState([]);
  const [isPressed, setIsPressed] = useState(false);
  const [errorRate, setErrorRate] = useState(0);

  useEffect(() => {
    // Load values from localStorage
    setLong(parseFloat(localStorage.getItem('long')) || 2.0);
    setShort(parseFloat(localStorage.getItem('short')) || 1.0);
    setHold(parseFloat(localStorage.getItem('hold')) || 1.5);
  }, []);

  const duration = location.state.duration || 5;
  const [time, setTime] = useState(() => DEBUG_TIME ?? location.state?.time ?? duration * 60);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [nextStep, setNextStep] = useState(2);
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 });
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 });
  const [showCurtain, setShowCurtain] = useState(false);

  // Keep word-related useEffects
  useEffect(() => {
    // Shuffle the fallback words
    const shuffledWords = [...FALLBACK_WORDS].sort(() => Math.random() - 0.5);
    setWords(shuffledWords);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let wordInterval;
    if (isPlaying && words.length > 0 && !isLoading) {
      setCurrentWord(words[wordIndex]);
      wordInterval = setInterval(() => {
        setWordIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % words.length;
          setCurrentWord(words[newIndex]);
          return newIndex;
        });
      }, wordChangeInterval);
    } else if (!isPlaying && hasStartedOnce && !isLoading) {
      setCurrentWord(words[wordIndex] || 'Play');
    } else if (!hasStartedOnce || isLoading) {
      setCurrentWord('Play');
    }
    return () => clearInterval(wordInterval);
  }, [isPlaying, words, isLoading, wordIndex, hasStartedOnce, wordChangeInterval]);

  // Timer useEffect
  useEffect(() => {
    let interval;
    if (isPlaying && time > 0) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime - 0.1;
          setProgress(100 - (newTime / (duration * 60)) * 100);
          return newTime > 0 ? newTime : 0;
        });
      }, 100);
    } else if (time === 0) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, time, duration]);

  // Animation useEffect
  useEffect(() => {
    let timeout;
    if (isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];
      const durations = [long, long, long, short, short, short, short];
      
      const fadeInNextCircle = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          
          // Update instruction text
          if (currentStep === 2 || currentStep === 3) {
            setInstructionText('Inhale');
          } else if (currentStep === 4) {
            setInstructionText('Hold');
          } else if (currentStep >= 5 && currentStep <= 7) {
            setInstructionText('Exhale');
          } else {
            setInstructionText('Hold');
          }

          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({ opacity: 1, transition: `opacity 0.2s ease-in` });
            setFadeOutStyle({ opacity: 0, transition: `opacity 0.2s ease-out` });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({ opacity: 1, transition: `opacity ${durations[nextStep - 1]}s ease-in` });
          }

          timeout = setTimeout(() => {
            setPreviousStep(null);
            timeout = setTimeout(() => {
              setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
              setPreviousStep(currentStep);
              fadeInNextCircle();
            }, hold * 1000);
          }, nextStep === 1 ? 200 : durations[nextStep - 1] * 1000);
        }, 10);
      };

      fadeInNextCircle();
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

  // Completion useEffect  
  useEffect(() => {
    if (time <= 0) {
      

      setIsPlaying(false);
      setShowCurtain(true);
      setTimeout(() => {
        navigate("/exercise2finish", {
          state: {
            timeSpent: timeRef.current,
            duration: duration,
            errorRate: errorRate
          },
        });
      }, 2000);
    }
  }, [time, navigate]);

  // Active time tracking
  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        timeRef.current += 1;
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  const startBreathing = () => {
    if (!isLoading && words.length > 0) {
      setIsPlaying(true);
      setNextStep(2);
      setFadeInStyle({ opacity: 1 });
      setShowPlayIcon(false);
      setShowStopIcon(false);
      if (!hasStartedOnce) {
        setHasStartedOnce(true);
        setWordIndex(0);
      }
      setCurrentWord(words[wordIndex]);
    }
  };

  const stopBreathing = () => {
    setIsPlaying(false);
    setShowStopIcon(true);
    setInstructionText('Pause');
    setCurrentStep(1);
    setNextStep(2);
    setPreviousStep(null);
    setFadeOutStyle({ opacity: 0 });
    setFadeInStyle({ opacity: 1 });
  };

  const resumeBreathing = () => {
    setIsPlaying(true);
    setShowStopIcon(false);
  };

  const handleEdit = () => {
    navigate('/RhythmChoosing', {
      state: {
        returnTo: '/exercise2workout',
        duration: duration,
        time: time
      }
    });
  };

  const handleClose = () => {
    navigate('/');
  };

// Spacebar feature
useEffect(() => {
  const handleKeyDown = (event) => {
    if (event.code === "Space") {
      setIsPressed(true);
      setErrorRate((prevError) => {
        const newError = prevError + 1;
        console.log("Spacebar pressed. New error rate:", newError);
        return newError;
      });
    }
  };

  const handleKeyUp = (event) => {
    if (event.code === "Space") {
      setIsPressed(false);
    }
  };

  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
  };
}, []);


  return (
    <div className="exercise-container">
      {showCurtain && <Curtain appear={true} duration={2000} />}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <span>{`${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`}</span>
        </div>
        <div className="button-container">
          <button className="save-button" onClick={handleEdit}>Edit Rhythm</button>
        </div>
      </div>

      <div className="word-display">
        <h2>{currentWord}</h2>
      </div>

      <div className="main-content">
        <div className="circle-container">
          <div className="circle-wrapper">
          {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing-stick left-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing-stick right-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}
            {[person1, person2, person3, person4, person5, person6, person7].map((PersonImg, index) => (
              <img
                key={`person-${index}`}
                src={PersonImg}
                alt={`person step ${index + 1}`}
                className="breathing-circle"
                style={{
                  position: 'absolute',
                  height: '55%',
                  width: 'auto',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...(currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 || (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne ? fadeOutStyle : { opacity: 1 }
                    : { opacity: 0 }),
                  cursor: 'pointer',
                  pointerEvents: currentStep === index + 1 ? 'auto' : 'none'
                }}
                onClick={() => {
                  if (currentStep === index + 1) {
                    stopBreathing();
                  }
                }}
              />
            ))}
            {showPlayIcon && (
              <img
                src={PlayIcon}
                alt="play icon"
                className="play-icon"
                onClick={startBreathing}
              />
            )}
            {showStopIcon && (
              <img
                src={StopIcon}
                alt="stop icon"
                className="play-icon"
                style={{ opacity: 1 }}
                onClick={resumeBreathing}
              />
            )}
          </div>
        </div>

        <div className="breathing-controls">
          <div className="control">
            <div className='row2'>
              <span>{instructionText}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={`spacebar-btn ${isPressed ? "pressed" : ""}`}>
            SPACE
          </div>

      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        <ul>
            <li>Rhythmically breathe while inhaling, holding, exhaling, and holding again. Repeat this cycle.</li>
            <li>Press the spacebar whenever you feel that you've made a mistake in word comprehension.</li>
          </ul>

      </div>

      {/* <div className="breathing-times">
        <div className="time-item">
          <span className="time-label">Long:</span>
          <span className="time-value">{long.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Short:</span>
          <span className="time-value">{short.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Hold:</span>
          <span className="time-value">{hold.toFixed(1)}s</span>
        </div>
      </div> */}
    </div>
  );
}

export default Exercise2Workout;
